import { reasonCopyKey } from 'components/Intelligence/reasonKey';
import cardConfidenceFrom, {
  CardConfidenceInput,
  ConfidenceDisplayRuleLookup,
  governingReasonCode
} from 'containers/Intelligence/data/cardConfidence';
import { ConfidenceBand, MetricConfidence } from 'containers/Intelligence/data/confidence';
import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { confidenceDisplayRuleFor as customer360DisplayRuleFor } from 'containers/Intelligence/registry/customer360Confidence';
import { confidenceDisplayRuleFor as customerInsightsDisplayRuleFor } from 'containers/Intelligence/registry/customerInsightsConfidence';
import i18n from 'utils/i18n';

import { OrbitConfidence, OrbitMetricResult } from './types';

/** The governed reason vocabulary is one catalogue; Orbit reads the same group both layers read. */
const REASONS_GROUP = `${TCustomerInsights}.ConfidenceGovernance.Reasons`;

/**
 * Everything `ConfidenceBadge` + `ConfidenceReason` need, in the shape those two already take:
 * `score` is a PERCENTAGE because the badge's copy is `{{band}} · {{score}}%`.
 */
export interface OrbitAnswerConfidence {
  band: ConfidenceBand;
  score: number | null;
  reasons: string[];
}

/**
 * Governed reason CODES into the governed reason COPY, dropping any the catalogue has no wording
 * for. Exported because the metric table prints `why_this_result.reason_codes` too, and a raw
 * `governed_non_scalar_grain` in a user-facing explanation is the pipeline's private language
 * (PLA-9563 §3a).
 */
export const humaniseReasonCodes = (codes: string[]): string[] =>
  (Array.isArray(codes) ? codes : [])
    .map(code => reasonCopyKey(REASONS_GROUP, code))
    .filter((key): key is string => key !== null)
    .map(key => i18n.t(key));

/**
 * The catalogue's `Show Confidence Score?` column for whichever layer an Orbit metric belongs to.
 *
 * Orbit answers from BOTH catalogues — Layer 1's `O-*` and Customer 360's `C-*` — and the two tables
 * carry disjoint identifier prefixes, so either order gives the same answer. `undefined` for an id
 * neither sheet carries, which `cardConfidenceFrom` treats as not eligible: a metric whose
 * eligibility the dashboard cannot state may not move an answer's score.
 */
const orbitDisplayRuleFor: ConfidenceDisplayRuleLookup = identifier =>
  customerInsightsDisplayRuleFor(identifier) ?? customer360DisplayRuleFor(identifier);

/**
 * One `metric_results[].confidence` (contract §6) in the shape the shared aggregator reads.
 *
 * The one field that is not a rename is `limitingReason`: the card layers carry a single
 * `confidence_limiting_reason` column and Orbit carries a `reason_codes` LIST, so the list is
 * reduced by the catalogue's own precedence rather than by position.
 */
const asMetricConfidence = (confidence: OrbitConfidence): MetricConfidence => ({
  score: typeof confidence.score === 'number' ? confidence.score : null,
  band: typeof confidence.level === 'string' ? confidence.level : null,
  limitingReason: governingReasonCode(confidence.reason_codes),
  capApplied: typeof confidence.cap_applied === 'boolean' ? confidence.cap_applied : null,
  handoffOk: typeof confidence.handoff_ok === 'boolean' ? confidence.handoff_ok : null
});

/**
 * The reading drawn when no eligible metric on the turn carried a score.
 *
 * `insufficient` with no score is what `ConfidenceBadge` already prints as "Confidence unavailable"
 * on the other two screens, and an empty `reasons` makes `ConfidenceReason` draw nothing: an answer
 * the catalogue cannot score says so, and explains nothing it was not told.
 */
const UNAVAILABLE: OrbitAnswerConfidence = { band: 'insufficient', score: null, reasons: [] };

/**
 * The confidence reading under an answered turn, in the vocabulary Customer Insights and Customer
 * 360 already draw (PLA-8815 / PLA-8828 `ConfidenceBadge` + `ConfidenceReason`).
 *
 * ONE source: `metric_results[].confidence` (contract §6), aggregated by the shared
 * `cardConfidenceFrom` so a turn's reading is the catalogue's mean over the metrics flagged
 * `Show Confidence Score? = Yes` (CTR-CONF-018/019), capped at 0.59 where a governed Critical reason
 * applies, with one leading reason off the governed codes.
 *
 * PLA-9569 removed two things this used to do, and neither may come back:
 *
 *   - the FIRST metric carrying a confidence was published as the whole answer's reading, so a turn
 *     over O-COM-008, O-COM-013, O-COM-020 and O-COM-007 showed O-COM-008's figure alone;
 *   - where no metric carried one, a band and a "Reason" were read out of `answer.confidence`, the
 *     model's own free text. That is a confidence Metabase never served, and prose is not a governed
 *     reading however confidently it opens.
 *
 * `freeText` is therefore accepted and deliberately unread. It stays in the signature because
 * `OrbitAnswer` passes `answer.confidence` positionally and the parameter's removal would be a
 * change to a file this fix does not own; the value has no reader left.
 */
export const orbitAnswerConfidence = (
  freeText: string,
  metricResults: OrbitMetricResult[]
): OrbitAnswerConfidence | null => {
  if (!Array.isArray(metricResults)) {
    return null;
  }

  const inputs: CardConfidenceInput[] = metricResults.map(result => ({
    identifiers: [result.metric_id],
    confidence: result.confidence ? asMetricConfidence(result.confidence) : undefined
  }));

  const aggregated = cardConfidenceFrom(orbitDisplayRuleFor, inputs);

  if (aggregated === undefined) {
    return UNAVAILABLE;
  }

  return { band: aggregated.band, score: aggregated.score, reasons: aggregated.reasons };
};

export default orbitAnswerConfidence;
